import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Icon, OTrackMark } from "@otrack/ui";

const MINUTE = 60_000;
const pad = (value: number) => String(value).padStart(2, "0");
const clock = (ts: number) => `${pad(new Date(ts).getHours())}:${pad(new Date(ts).getMinutes())}`;

/** The screens before sign-in have no saved theme yet, so they follow the system. */
function useSystemTheme() {
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => { document.documentElement.dataset["theme"] = query.matches ? "dark" : "light"; };
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);
}

function useNow(interval: number): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), interval);
    return () => window.clearInterval(id);
  }, [interval]);
  return now;
}

type ProjectColor = "indigo" | "teal" | "gold" | "rose" | "sage" | "slate";
type Layer = { from: number; to: number; kind: "logged" | "unbilled" | "planned" };
type Lane = { color: ProjectColor | "running"; layers: Layer[] };

/**
 * A day in Strata, in minutes around the moment the screen opened: one layer per Project in its
 * Project Color, the running Timer in Ember on the middle layer (like the mark), and what is still
 * planned as outlines.
 */
const LANES: Lane[] = [
  { color: "indigo", layers: [{ from: -410, to: -352, kind: "logged" }, { from: -150, to: -118, kind: "logged" }] },
  { color: "teal", layers: [{ from: -346, to: -300, kind: "logged" }, { from: 30, to: 96, kind: "planned" }] },
  { color: "gold", layers: [{ from: -294, to: -270, kind: "unbilled" }, { from: -112, to: -64, kind: "logged" }] },
  { color: "running", layers: [] },
  { color: "rose", layers: [{ from: -262, to: -205, kind: "logged" }, { from: 112, to: 190, kind: "planned" }] },
  { color: "sage", layers: [{ from: -198, to: -158, kind: "logged" }] },
  { color: "slate", layers: [{ from: -428, to: -414, kind: "unbilled" }, { from: 62, to: 124, kind: "planned" }] }
];
const RUNNING_SINCE = -56;
const WINDOW = { before: 430, after: 200 };

function elapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 3600)}:${pad(Math.floor(seconds / 60) % 60)}:${pad(seconds % 60)}`;
}

function StrataField({ now }: { now: number }) {
  const [opened] = useState(Date.now);
  const start = opened - WINDOW.before * MINUTE;
  const span = (WINDOW.before + WINDOW.after) * MINUTE;
  const x = (ts: number) => `${((ts - start) / span) * 100}%`;
  const width = (from: number, to: number) => `${(Math.max(0, to - from) / span) * 100}%`;
  const at = (minutes: number) => opened + minutes * MINUTE;
  const delay = (minutes: number) => `${(minutes + WINDOW.before) * 1.6}ms`;
  const hours: number[] = [];
  for (let hour = Math.ceil(start / (60 * MINUTE)) * 60 * MINUTE; hour <= start + span; hour += 60 * MINUTE) hours.push(hour);
  const running = at(RUNNING_SINCE);

  return <div className="ot-auth-field" aria-hidden="true">
    <div className="ot-auth-field__day">
      {hours.map((hour) => <span key={hour} className="ot-auth-field__hour" style={{ left: x(hour) }}><b>{pad(new Date(hour).getHours())}<span>:00</span></b></span>)}
      {LANES.map((lane, index) => <div key={lane.color} className="ot-auth-field__lane" style={{ "--lane": index, "--pc": lane.color === "running" ? undefined : `var(--ot-pc-${lane.color})` } as CSSProperties}>
        {lane.layers.map((layer) => <i
          key={layer.from}
          className={`ot-auth-layer is-${layer.kind}`}
          style={{ left: x(at(layer.from)), width: width(at(layer.from), at(layer.to)), "--delay": delay(layer.from) } as CSSProperties}
        />)}
        {lane.color === "running" && <i className="ot-auth-layer is-running" style={{ left: x(running), width: width(running, now), "--delay": delay(RUNNING_SINCE) } as CSSProperties}>
          <b>{elapsed(now - running)}</b>
        </i>}
      </div>)}
      <span className="ot-auth-field__now" style={{ left: x(now) }}>
        <b className="ot-auth-field__clock">{clock(now)}<small>:{pad(new Date(now).getSeconds())}</small></b>
      </span>
    </div>
  </div>;
}

function greeting(now: number): string {
  const hour = new Date(now).getHours();
  if (hour < 5) return "Working late";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function AuthLayout({ children }: { children: ReactNode }) {
  useSystemTheme();
  const now = useNow(1000);
  return <main className="ot-auth">
    <StrataField now={now}/>
    <header className="ot-auth__brand"><OTrackMark size={24}/><span>ontrack</span></header>
    <section className="ot-auth__body" aria-labelledby="ot-auth-title">
      <p className="ot-auth__greeting">{greeting(now)}</p>
      {children}
    </section>
    <footer className="ot-auth__footer"><Icon name="lock"/>Private to approved Google accounts</footer>
  </main>;
}

/** Shown while Clerk loads or this account's data opens; it fades in only if that takes a moment. */
export function Splash() {
  useSystemTheme();
  return <main className="ot-auth-splash" aria-busy="true">
    <OTrackMark size={40} running/>
    <span className="ot-sr-only" role="status">Opening ontrack</span>
  </main>;
}

const RESTRICTED = /not_allowed|restricted|blocked|waitlist/;

/** A sign-in error in the User's words; Clerk's codes for refused accounts get their own line. */
export function describeSignInError(error: { code?: string | undefined; longMessage?: string | undefined; message?: string | undefined } | null | undefined): string | null {
  if (!error) return null;
  if (RESTRICTED.test(error.code ?? "")) return "This Google account doesn't have access to ontrack. Try another one.";
  return error.longMessage ?? error.message ?? "Google sign-in didn't finish. Try again.";
}

/** Google's own "G", as its sign-in guidelines ask for; it sits on a light chip in both themes. */
function GoogleG() {
  return <span className="ot-auth__g"><svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.87 2.68-6.62z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18z"/>
    <path fill="#FBBC05" d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3-2.33z"/>
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.59A8.65 8.65 0 0 0 9 0 9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z"/>
  </svg></span>;
}

/**
 * Starts a Google sign-in. `start` resolves with an error to show, or null once Google is on its
 * way: the web app is navigating away by then, while the desktop app waits for the browser and
 * lets the User open it again (`reopenable`).
 */
function GoogleButton({ start, initialError = null, reopenable = false }: { start: () => Promise<string | null>; initialError?: string | null; reopenable?: boolean }) {
  const [state, setState] = useState<"idle" | "pending" | "waiting">("idle");
  const [error, setError] = useState<string | null>(initialError);

  const begin = async () => {
    setState("pending");
    setError(null);
    const problem = await start().catch(() => "Google sign-in couldn't start. Check your connection and try again.");
    if (problem) {
      setState("idle");
      setError(problem);
    } else if (reopenable) {
      setState("waiting");
    }
  };

  const label = state === "pending" ? "Opening Google" : state === "waiting" ? "Continue in your browser" : "Continue with Google";
  return <div className="ot-auth__actions">
    <button type="button" className="ot-auth__google" onClick={() => void begin()} disabled={state === "pending"} aria-busy={state !== "idle"}>
      <GoogleG/>
      <span>{label}</span>
      <Icon name={state === "waiting" ? "refresh" : "arrow-right"} className="ot-auth__google-arrow"/>
    </button>
    <p className="ot-auth__error" role="alert">{error && <><Icon name="warning"/>{error}</>}</p>
  </div>;
}

export function SignInScreen({ start, error = null, reopenable = false }: { start: () => Promise<string | null>; error?: string | null; reopenable?: boolean }) {
  return <AuthLayout>
    <h1 id="ot-auth-title">Every hour, accounted for.</h1>
    <p className="ot-auth__lede">Sign in to pick up your running timer, today's hours, and this month's earnings on any device.</p>
    <GoogleButton start={start} initialError={error} reopenable={reopenable}/>
  </AuthLayout>;
}

/** A dead end the User can only retry: sign-in unreachable, or this browser's storage blocked. */
export function AuthProblem({ title, children, retry = true }: { title: string; children: ReactNode; retry?: boolean }) {
  return <AuthLayout>
    <h1 id="ot-auth-title">{title}</h1>
    <p className="ot-auth__lede">{children}</p>
    {retry && <div className="ot-auth__actions">
      <button type="button" className="ot-auth__google is-quiet" onClick={() => window.location.reload()}>
        <Icon name="refresh"/><span>Try again</span>
      </button>
    </div>}
  </AuthLayout>;
}
